// bearings types — wisdom card press for orientation recovery

export type Granularity = 'local' | 'congregation' | 'tradition' | 'public' | 'human';

export type SourceType = 'sermon' | 'scripture' | 'news' | 'social' | 'conversation' | 'book' | 'other';

export const EMOTIONS = [
  'troubled', 'grieved', 'angry', 'fearful', 'ashamed', 'defensive', 'numb', 'weary',
  'curious', 'tender', 'hopeful', 'open', 'lightened', 'confused'
] as const;

export type Emotion = typeof EMOTIONS[number];

export const SOURCE_TYPES: { value: SourceType; label: string }[] = [
  { value: 'sermon', label: 'Sermon or teaching' },
  { value: 'scripture', label: 'Scripture + interpretation' },
  { value: 'news', label: 'News or public statement' },
  { value: 'social', label: 'Social post or reel' },
  { value: 'conversation', label: 'Conversation (family, friend, elder)' },
  { value: 'book', label: 'Book, podcast, or article' },
  { value: 'other', label: 'Other source' },
];

export const GRANULARITIES: { value: Granularity; label: string; desc: string }[] = [
  { value: 'local', label: 'My own heart', desc: 'Mostly personal' },
  { value: 'congregation', label: 'Our congregation', desc: 'Local body' },
  { value: 'tradition', label: 'Our tradition', desc: 'Denomination or stream' },
  { value: 'public', label: 'Public discourse', desc: 'Culture, media, politics' },
  { value: 'human', label: 'Being human', desc: 'Something universal' },
];

export const ACTION_READINESSES = [
  'listen longer before answering',
  'pray with the tension',
  'talk with someone I trust',
  'ask a pastor or elder directly',
  'examine my own sources and habits',
  'read the primary texts myself',
  'extend patience to someone who sees it differently',
  'bring this to my small group',
  'shift how I give or serve',
  'release the need to decide today',
  'advocate or speak in my community',
  'forgive where I have been quick to judge',
] as const;

export interface WisdomPress {
  id: string;
  pilgrim_hash?: string | null;
  source_type: SourceType;
  claim_summary: string;
  tension_text?: string | null;
  granularity: Granularity;
  before_emotion: Emotion;
  before_intensity: number; // 1-5
  after_emotion: Emotion;
  after_intensity: number;
  action_readiness: string[]; // parsed from JSON
  evolution_note?: string | null;
  created_at: string;
  last_tended_at?: string | null;
  is_public: number;
}

export interface CardEvolution {
  id: string;
  press_id: string;
  after_emotion: Emotion;
  after_intensity: number;
  note?: string | null;
  created_at: string;
}

export interface PressInput {
  source_type: SourceType;
  claim_summary: string;
  tension_text?: string;
  granularity: Granularity;
  before_emotion: Emotion;
  before_intensity: number;
  after_emotion: Emotion;
  after_intensity: number;
  action_readiness: string[];
  evolution_note?: string;
}

export interface MapFilters {
  granularity?: Granularity;
  source_type?: SourceType;
  emotion_family?: 'distress' | 'heavy' | 'open' | 'tender';
  has_tension?: boolean;
  search?: string;
}

export interface Insights {
  emotion_journeys: Array<{
    before: Emotion;
    after: Emotion;
    count: number;
  }>;
  common_tensions: Array<{
    tension: string;
    count: number;
    example_ids: string[];
  }>;
  readiness_surface: Array<{
    action: string;
    count: number;
  }>;
  cluster_reflection?: string; // gentle AI-shaped note, disclosed
  total_presses: number;
}

export interface Resonance {
  id: string;
  press: WisdomPress;
  score: number;
}

export interface MyPress extends WisdomPress {
  evolutions?: CardEvolution[];
}

export type Tab = 'atlas' | 'press' | 'my' | 'insights';
